import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, ShieldCheck, Clock, Users } from 'lucide-react';
import { SERVICES } from '../data/mock';
import { Reveal, Magnetic, AnimatedDivider } from './effects';

const PILLARS = [
  { Icon: Clock, label: 'Vardiya Hassasiyeti', sub: 'Dakika bazlı planlama' },
  { Icon: Truck, label: 'İzotermli Teslim', sub: 'GPS takipli sıcak zincir' },
  { Icon: ShieldCheck, label: 'HACCP Disiplini', sub: '14 noktalı kalite kontrol' },
  { Icon: Users, label: 'Tek Muhatap', sub: 'Atanmış müşteri yöneticisi' },
];

const ServicesPreview = () => {
  const service = SERVICES[0];

  return (
    <section className="bg-[#F5F3EF]">
      {/* Header */}
      <div className="container-x pt-28 lg:pt-40 pb-14 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-7">
              <AnimatedDivider width="3rem" />
              <Reveal delay={100} y={8} duration={700}>
                <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
                  Hizmetimiz
                </span>
              </Reveal>
            </div>
            <Reveal delay={200}>
              <h2
                className="font-display text-[40px] lg:text-[60px] text-[#1A1A1A] leading-[1.04] tracking-[-0.02em]"
                style={{ fontWeight: 500 }}
              >
                Tek hizmet,{' '}
                <span className="italic font-light text-[#9A7F4F]">tek disiplin</span>.
              </h2>
            </Reveal>
            <Reveal delay={400}>
              <p className="mt-7 max-w-xl text-[15.5px] lg:text-[16.5px] text-[#1A1A1A]/68 leading-[1.7]">
                Tek bir işi en iyi şekilde yapmaya odaklandık: <strong className="font-medium text-[#1A1A1A]">Taşıma Yemek Hizmeti.</strong> 14 yıldır rafine ettiğimiz operasyonel disiplinle, Türkiye'nin sanayi kalbinde sertifikalı bir sistem teslim ediyoruz.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <Reveal delay={400}>
              <Link
                to="/tasima-yemek"
                className="group inline-flex items-center gap-2 text-[#1A1A1A] text-[12.5px] tracking-[0.18em] uppercase font-medium border-b border-[#1A1A1A]/30 pb-1.5 hover:border-[#9A7F4F] hover:text-[#9A7F4F] transition-colors"
              >
                Detaylı İncele
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>

      {/* SINGLE editorial strip — Taşıma Yemek */}
      <Reveal y={40} duration={1000}>
        <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch group/strip">
          <div className="relative overflow-hidden aspect-[4/3] lg:aspect-auto lg:min-h-[580px]">
            <img
              src={`${service.image}?auto=format&fit=crop&q=85&w=1600`}
              alt={service.title}
              className="absolute inset-0 w-full h-full object-cover transition-all duration-[1400ms] ease-out group-hover/strip:scale-[1.05] group-hover/strip:saturate-[1.1]"
              style={{ filter: 'saturate(0.9)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#72272D]/25 via-transparent to-transparent transition-opacity duration-700 group-hover/strip:opacity-0" />

            <div className="absolute top-7 left-7 lg:top-9 lg:left-9 flex items-center gap-3">
              <div className="font-display numeric text-[44px] lg:text-[56px] text-[#F5F0E5] leading-none drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]">
                01
              </div>
              <div className="h-px w-12 bg-[#C9A875] transition-all duration-700 group-hover/strip:w-16" />
            </div>

            {/* Floating tag */}
            <div className="absolute bottom-7 left-7 lg:bottom-9 lg:left-9">
              <span className="inline-block bg-[#F5F3EF]/95 backdrop-blur-sm text-[#1A1A1A] px-4 py-2 text-[10.5px] tracking-[0.32em] uppercase font-medium">
                {service.badge}
              </span>
            </div>
          </div>

          <div className="px-6 sm:px-12 lg:px-20 py-16 lg:py-24 flex items-center bg-[#FAF8F4]">
            <div className="max-w-md">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#9A7F4F] font-medium mb-6 leading-relaxed">
                {service.audience}
              </div>

              <h3
                className="font-display text-[34px] sm:text-[42px] lg:text-[54px] leading-[1.04] tracking-[-0.02em] text-[#1A1A1A]"
                style={{ fontWeight: 500 }}
              >
                {service.title}
              </h3>

              <p className="mt-6 text-[15px] lg:text-[16px] text-[#1A1A1A]/72 leading-[1.7]">
                {service.desc}
              </p>

              {/* 4 mini pillars — operational specs */}
              <div className="mt-9 grid grid-cols-2 gap-x-6 gap-y-5">
                {PILLARS.map((p) => (
                  <div key={p.label} className="flex items-start gap-3">
                    <div
                      className="flex-none h-9 w-9 rounded-sm grid place-items-center"
                      style={{ background: 'rgba(114,39,45,0.06)', color: '#72272D' }}
                    >
                      <p.Icon size={16} strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="text-[12.5px] font-medium text-[#1A1A1A] leading-tight">
                        {p.label}
                      </div>
                      <div className="text-[11px] text-[#1A1A1A]/55 mt-0.5">{p.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              <Magnetic strength={0.16}>
                <Link
                  to="/tasima-yemek"
                  className="group mt-10 inline-flex items-center gap-2 text-[#1A1A1A] text-[12.5px] tracking-[0.18em] uppercase font-medium border-b border-[#1A1A1A]/30 pb-1.5 hover:border-[#9A7F4F] hover:text-[#9A7F4F] transition-colors"
                >
                  Detayları İncele
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </Magnetic>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default ServicesPreview;
